import React, { useEffect, useCallback, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { AuthService } from '../../services/authService';
import { useAuthStore } from '../../stores/useAuthStore';
import { useAuthModalStore } from '../../stores/useAuthModalStore';
import { EyeIcon, EyeOffIcon } from '../icons/Icons';
import toast from 'react-hot-toast';
import { MAINTENANCE_MODE, MAINTENANCE_MESSAGE, isEmailAllowed } from '../../config/maintenance';

type AuthTab = 'login' | 'register';

const AuthModal: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { isOpen, redirectPath, closeModal, executePendingAction } = useAuthModalStore();
    const { login, isAuthenticated } = useAuthStore();

    // Tab state
    const [activeTab, setActiveTab] = useState<AuthTab>('login');

    // Login form state
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [loginLoading, setLoginLoading] = useState(false);
    const [loginError, setLoginError] = useState('');

    // Register form state
    const [registerName, setRegisterName] = useState('');
    const [registerEmail, setRegisterEmail] = useState('');
    const [registerCompany, setRegisterCompany] = useState('');
    const [registerPassword, setRegisterPassword] = useState('');
    const [registerPasswordConfirm, setRegisterPasswordConfirm] = useState('');
    const [showRegisterPassword, setShowRegisterPassword] = useState(false);
    const [registerLoading, setRegisterLoading] = useState(false);
    const [registerError, setRegisterError] = useState('');
    const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});

    // Reset form when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            setActiveTab('login');
            setLoginEmail('');
            setLoginPassword('');
            setLoginError('');
            setRegisterName('');
            setRegisterEmail('');
            setRegisterCompany('');
            setRegisterPassword('');
            setRegisterPasswordConfirm('');
            setRegisterError('');
            setValidationErrors({});
        }
    }, [isOpen]);

    // Handle OAuth callback when modal receives token
    useEffect(() => {
        const token = searchParams.get('token');
        const error = searchParams.get('error');
        const provider = searchParams.get('provider');

        if (token && isOpen) {
            AuthService.handleSocialCallback(token)
                .then((response) => {
                    login(response.user);
                    const providerName = provider === 'google' ? 'Google' : 'GitHub';
                    toast.success(`Login com ${providerName} realizado com sucesso!`);

                    // Clean URL params
                    searchParams.delete('token');
                    searchParams.delete('provider');
                    setSearchParams(searchParams, { replace: true });

                    // Execute pending action or redirect
                    executePendingAction();
                    closeModal();

                    if (redirectPath) {
                        navigate(redirectPath);
                    }
                })
                .catch((err) => {
                    console.error('[AuthModal] Social callback error:', err);
                    toast.error('Erro ao processar login social. Tente novamente.');
                    // Clean URL params
                    searchParams.delete('token');
                    searchParams.delete('error');
                    searchParams.delete('provider');
                    setSearchParams(searchParams, { replace: true });
                });
        } else if (error && isOpen) {
            const providerName = provider === 'google' ? 'Google' : provider === 'github' ? 'GitHub' : 'social';
            toast.error(`Falha na autenticação com ${providerName}. Tente novamente.`);
            // Clean URL params
            searchParams.delete('token');
            searchParams.delete('error');
            searchParams.delete('provider');
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, isOpen, login, navigate, redirectPath, closeModal, executePendingAction, setSearchParams]);

    // Close modal and redirect if user becomes authenticated
    useEffect(() => {
        if (isAuthenticated && isOpen) {
            executePendingAction();
            if (redirectPath) {
                navigate(redirectPath);
            }
            closeModal();
        }
    }, [isAuthenticated, isOpen, closeModal, executePendingAction, redirectPath, navigate]);

    // Handle escape key
    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            closeModal();
        }
    }, [closeModal]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, handleKeyDown]);

    // Handle login submit
    const handleLoginSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoginError('');

        // Check maintenance mode before attempting login
        if (MAINTENANCE_MODE && !isEmailAllowed(loginEmail)) {
            setLoginError(MAINTENANCE_MESSAGE);
            toast.error(MAINTENANCE_MESSAGE);
            return;
        }

        setLoginLoading(true);

        try {
            const response = await AuthService.login({ email: loginEmail, password: loginPassword });
            login(response.user);
            toast.success('Login realizado com sucesso!');
            executePendingAction();
            closeModal();
            if (redirectPath) {
                navigate(redirectPath);
            }
        } catch (error: any) {
            const errorMessage = error.message || 'Email ou senha inválidos.';
            setLoginError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoginLoading(false);
        }
    };

    // Handle register submit
    const handleRegisterSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setRegisterError('');
        setValidationErrors({});

        if (registerPassword !== registerPasswordConfirm) {
            setValidationErrors({ password_confirmation: ['As senhas não coincidem'] });
            toast.error('As senhas não coincidem.');
            return;
        }

        setRegisterLoading(true);

        try {
            const response = await AuthService.register({
                name: registerName,
                email: registerEmail,
                company: registerCompany,
                role: 'user',
                password: registerPassword,
                confirmPassword: registerPasswordConfirm
            });
            login(response.user);
            toast.success('Conta criada com sucesso!');
            executePendingAction();
            closeModal();
            if (redirectPath) {
                navigate(redirectPath);
            }
        } catch (error: any) {
            if (error.validationErrors) {
                setValidationErrors(error.validationErrors);
                toast.error('Por favor, corrija os erros no formulário.');
            } else {
                const errorMessage = error.message || 'Falha no registro. Verifique seus dados.';
                setRegisterError(errorMessage);
                toast.error(errorMessage);
            }
        } finally {
            setRegisterLoading(false);
        }
    };

    if (!isOpen) return null;

    const isLoading = loginLoading || registerLoading;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Overlay */}
            <div
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={closeModal}
            />

            {/* Modal */}
            <div className="relative bg-snapfy-surface rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 animate-in fade-in zoom-in duration-200 max-h-[90vh] overflow-y-auto border border-snapfy-border">
                {/* Maintenance Mode Banner */}
                {MAINTENANCE_MODE && (
                    <div className="mb-4 -mt-2 -mx-2 p-3 bg-amber-500/20 border border-amber-500/50 rounded-xl">
                        <div className="flex items-center gap-2">
                            <svg className="h-5 w-5 text-amber-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <span className="text-amber-300 text-sm font-medium">
                                🚧 Sistema em manutenção - Acesso restrito
                            </span>
                        </div>
                    </div>
                )}

                {/* Close button */}
                <button
                    onClick={closeModal}
                    className="absolute top-4 right-4 text-snapfy-text-secondary hover:text-snapfy-text-primary transition-colors"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                {/* Title */}
                <h2 className="text-xl font-semibold text-snapfy-text-primary text-center mb-2 mt-2">
                    {activeTab === 'login' ? 'Entre na sua conta' : 'Crie sua conta'}
                </h2>
                <p className="text-snapfy-text-secondary text-center text-sm mb-6">
                    {activeTab === 'login'
                        ? 'Acesse para gerar e editar imagens.'
                        : 'Comece a criar imagens incríveis.'}
                </p>

                {/* Ambiente interno: sem aba "Criar conta". O cadastro publico nao
                    existe aqui (a rota da API foi removida e dava 404); as contas
                    sao criadas pelo admin. Sem a aba, o formulario de registro fica
                    inalcancavel e o modal abre sempre no login. */}

                {/* Social Login Buttons */}
                <div className="space-y-3 mb-6">
                    <a
                        href={AuthService.getSocialLoginUrl('google')}
                        className="w-full flex items-center justify-center gap-3 py-2.5 px-4 border border-snapfy-border rounded-xl bg-snapfy-surface-hover text-snapfy-text-primary font-medium hover:bg-snapfy-inner hover:border-snapfy-border transition-all duration-200"
                    >
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        Continuar com Google
                    </a>

                    <a
                        href={AuthService.getSocialLoginUrl('github')}
                        className="w-full flex items-center justify-center gap-3 py-2.5 px-4 border border-snapfy-border rounded-xl bg-snapfy-surface-hover text-snapfy-text-primary font-medium hover:bg-snapfy-inner hover:border-snapfy-border transition-all duration-200"
                    >
                        <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z" clipRule="evenodd" />
                        </svg>
                        Continuar com GitHub
                    </a>
                </div>

                {/* Divider */}
                <div className="relative mb-6">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-snapfy-border" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-snapfy-surface text-snapfy-text-secondary">
                            ou com email
                        </span>
                    </div>
                </div>

                {/* Login Form */}
                {activeTab === 'login' && (
                    <form onSubmit={handleLoginSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="login-email" className="block text-sm font-medium text-snapfy-text-secondary mb-1">
                                Email
                            </label>
                            <input
                                id="login-email"
                                type="email"
                                required
                                value={loginEmail}
                                onChange={(e) => setLoginEmail(e.target.value)}
                                className="w-full px-3 py-2 border border-snapfy-border rounded-xl bg-snapfy-surface-hover text-snapfy-text-primary placeholder-snapfy-text-secondary focus:outline-none focus:ring-2 focus:ring-[#7063f7] focus:border-transparent"
                                placeholder="seu@email.com"
                            />
                        </div>

                        <div>
                            <label htmlFor="login-password" className="block text-sm font-medium text-snapfy-text-secondary mb-1">
                                Senha
                            </label>
                            <div className="relative">
                                <input
                                    id="login-password"
                                    type={showLoginPassword ? 'text' : 'password'}
                                    required
                                    value={loginPassword}
                                    onChange={(e) => setLoginPassword(e.target.value)}
                                    className="w-full px-3 py-2 border border-snapfy-border rounded-xl bg-snapfy-surface-hover text-snapfy-text-primary placeholder-snapfy-text-secondary focus:outline-none focus:ring-2 focus:ring-[#7063f7] focus:border-transparent pr-10"
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-snapfy-text-secondary hover:text-snapfy-text-primary"
                                >
                                    {showLoginPassword ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                                </button>
                            </div>
                        </div>

                        {loginError && (
                            <div className="rounded-md bg-red-500/10 border border-red-500/50 p-3">
                                <p className="text-sm text-red-400">{loginError}</p>
                            </div>
                        )}

                        <div className="flex items-center justify-end">
                            <Link
                                to="/forgot-password"
                                onClick={closeModal}
                                className="text-sm text-snapfy-accent-soft hover:text-snapfy-accent-soft"
                            >
                                Esqueceu sua senha?
                            </Link>
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full py-2.5 px-4 bg-[#7063f7] text-white font-medium rounded-xl hover:bg-[#5a4dd4] focus:outline-none focus:ring-2 focus:ring-[#7063f7] focus:ring-offset-2 focus:ring-offset-[#1C1C1C] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {loginLoading ? (
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
                            ) : (
                                'Entrar'
                            )}
                        </button>
                    </form>
                )}

                {/* Register Form */}
                {activeTab === 'register' && (
                    <form onSubmit={handleRegisterSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="register-name" className="block text-sm font-medium text-snapfy-text-secondary mb-1">
                                Nome completo
                            </label>
                            <input
                                id="register-name"
                                type="text"
                                required
                                value={registerName}
                                onChange={(e) => setRegisterName(e.target.value)}
                                className={`w-full px-3 py-2 border rounded-xl bg-snapfy-surface-hover text-snapfy-text-primary placeholder-snapfy-text-secondary focus:outline-none focus:ring-2 focus:ring-[#7063f7] focus:border-transparent ${validationErrors.name ? 'border-red-500' : 'border-snapfy-border'
                                    }`}
                                placeholder="Seu nome"
                            />
                            {validationErrors.name && (
                                <p className="mt-1 text-sm text-red-400">{validationErrors.name[0]}</p>
                            )}
                        </div>

                        <div>
                            <label htmlFor="register-email" className="block text-sm font-medium text-snapfy-text-secondary mb-1">
                                Email
                            </label>
                            <input
                                id="register-email"
                                type="email"
                                required
                                value={registerEmail}
                                onChange={(e) => setRegisterEmail(e.target.value)}
                                className={`w-full px-3 py-2 border rounded-xl bg-snapfy-surface-hover text-snapfy-text-primary placeholder-snapfy-text-secondary focus:outline-none focus:ring-2 focus:ring-[#7063f7] focus:border-transparent ${validationErrors.email ? 'border-red-500' : 'border-snapfy-border'
                                    }`}
                                placeholder="seu@email.com"
                            />
                            {validationErrors.email && (
                                <p className="mt-1 text-sm text-red-400">{validationErrors.email[0]}</p>
                            )}
                        </div>

                        <div>
                            <label htmlFor="register-company" className="block text-sm font-medium text-snapfy-text-secondary mb-1">
                                Empresa
                            </label>
                            <input
                                id="register-company"
                                type="text"
                                required
                                value={registerCompany}
                                onChange={(e) => setRegisterCompany(e.target.value)}
                                className={`w-full px-3 py-2 border rounded-xl bg-snapfy-surface-hover text-snapfy-text-primary placeholder-snapfy-text-secondary focus:outline-none focus:ring-2 focus:ring-[#7063f7] focus:border-transparent ${validationErrors.company ? 'border-red-500' : 'border-snapfy-border'
                                    }`}
                                placeholder="Nome da empresa"
                            />
                            {validationErrors.company && (
                                <p className="mt-1 text-sm text-red-400">{validationErrors.company[0]}</p>
                            )}
                        </div>

                        <div>
                            <label htmlFor="register-password" className="block text-sm font-medium text-snapfy-text-secondary mb-1">
                                Senha
                            </label>
                            <div className="relative">
                                <input
                                    id="register-password"
                                    type={showRegisterPassword ? 'text' : 'password'}
                                    required
                                    value={registerPassword}
                                    onChange={(e) => setRegisterPassword(e.target.value)}
                                    className={`w-full px-3 py-2 border rounded-xl bg-snapfy-surface-hover text-snapfy-text-primary placeholder-snapfy-text-secondary focus:outline-none focus:ring-2 focus:ring-[#7063f7] focus:border-transparent pr-10 ${validationErrors.password ? 'border-red-500' : 'border-snapfy-border'
                                        }`}
                                    placeholder="Mínimo 8 caracteres"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-snapfy-text-secondary hover:text-snapfy-text-primary"
                                >
                                    {showRegisterPassword ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                                </button>
                            </div>
                            {validationErrors.password && (
                                <p className="mt-1 text-sm text-red-400">{validationErrors.password[0]}</p>
                            )}
                        </div>

                        <div>
                            <label htmlFor="register-password-confirm" className="block text-sm font-medium text-snapfy-text-secondary mb-1">
                                Confirmar senha
                            </label>
                            <input
                                id="register-password-confirm"
                                type="password"
                                required
                                value={registerPasswordConfirm}
                                onChange={(e) => setRegisterPasswordConfirm(e.target.value)}
                                className={`w-full px-3 py-2 border rounded-xl bg-snapfy-surface-hover text-snapfy-text-primary placeholder-snapfy-text-secondary focus:outline-none focus:ring-2 focus:ring-[#7063f7] focus:border-transparent ${validationErrors.password_confirmation ? 'border-red-500' : 'border-snapfy-border'
                                    }`}
                                placeholder="Repita a senha"
                            />
                            {validationErrors.password_confirmation && (
                                <p className="mt-1 text-sm text-red-400">{validationErrors.password_confirmation[0]}</p>
                            )}
                        </div>

                        {registerError && (
                            <div className="rounded-md bg-red-500/10 border border-red-500/50 p-3">
                                <p className="text-sm text-red-400">{registerError}</p>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full py-2.5 px-4 bg-[#7063f7] text-white font-medium rounded-xl hover:bg-[#5a4dd4] focus:outline-none focus:ring-2 focus:ring-[#7063f7] focus:ring-offset-2 focus:ring-offset-[#1C1C1C] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {registerLoading ? (
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
                            ) : (
                                'Criar conta'
                            )}
                        </button>

                        <p className="text-xs text-snapfy-text-secondary text-center">
                            Ao criar uma conta, você concorda com nossos{' '}
                            <Link to="/terms" onClick={closeModal} className="text-snapfy-accent-soft hover:text-snapfy-accent-soft">
                                Termos de Uso
                            </Link>{' '}
                            e{' '}
                            <Link to="/privacy" onClick={closeModal} className="text-snapfy-accent-soft hover:text-snapfy-accent-soft">
                                Política de Privacidade
                            </Link>
                        </p>
                    </form>
                )}
            </div>
        </div>
    );
};

export default AuthModal;
